import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  Typography,
  Box,
  Divider,
  Button,
  CircularProgress,
  MenuItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Badge,
  IconButton,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import {
  ChatBubble as MessageIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const rawBase = (process.env.REACT_APP_API_URL || process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
const API_BASE_URL = rawBase.endsWith('/api') ? rawBase : `${rawBase}/api`;

interface Conversation {
  id: string;
  participantId: string;
  participantName: string;
  participantAvatar?: string;
  lastMessage?: {
    id: string;
    content: string;
    senderId: string;
    receiverId: string;
    timestamp: string;
    read: boolean;
  };
  unreadCount: number;
}

interface MessagesMenuProps {
  messagesCount?: number;
  onCountChange?: () => void;
}

const MessagesMenu: React.FC<MessagesMenuProps> = ({ messagesCount = 0, onCountChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  // Load messages when menu opens
  useEffect(() => {
    if (anchorEl) {
      loadMessages();
    } else {
      // When menu closes, refresh the unread count
      if (conversations.length > 0) {
        onCountChange?.();
      }
    }
  }, [anchorEl, onCountChange, conversations.length]);

  const loadMessages = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/messages/conversations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data.data || []);
      } else {
        console.error('Failed to fetch conversations');
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleConversationClick = (conversation: Conversation) => {
    handleCloseMenu();
    // Navigate to messages page with the specific user
    navigate(`/messages?userId=${conversation.participantId}`);
  };

  const handleDeleteConversation = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    // TODO: Implement delete conversation
  };

  const handleViewAll = () => {
    handleCloseMenu();
    navigate('/messages');
  };

  const unreadCount = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);
  const visibleConversations = conversations.slice(0, 5);

  return (
    <>
      <Tooltip title="Messages">
        <IconButton
          color="inherit"
          onClick={handleOpenMenu}
          aria-label="View messages"
          size="small"
          sx={{ ml: { xs: 0.25, sm: 1 }, p: { xs: 0.5, sm: 1 } }}
        >
          <Badge 
            badgeContent={messagesCount || unreadCount} 
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                minWidth: { xs: 16, sm: 20 },
                height: { xs: 16, sm: 20 },
              }
            }}
          >
            <MessageIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Messages
            {isLoading && (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            )}
          </Typography>
        </Box>

        <Divider />

        {/* Messages List */}
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : conversations.length === 0 ? (
          <>
            <Box sx={{ p: 2, textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="textSecondary">No messages</Typography>
            </Box>
            
            {/* Fixed "View All Messages" button at bottom */}
            <Divider />
            <Box sx={{
              p: 1,
              display: 'flex',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Button
                size="small"
                onClick={handleViewAll}
                fullWidth
              >
                View All Messages
              </Button>
            </Box>
          </>
        ) : (
          <>
            {/* Scrollable conversations list */}
            <Box sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden'
            }}>
              {visibleConversations.map((conversation) => {
                const hasUnread = conversation.unreadCount > 0;
                const lastMessage = conversation.lastMessage;
                
                return (
                  <MenuItem
                    key={conversation.id}
                    onClick={() => handleConversationClick(conversation)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      backgroundColor: hasUnread
                        ? alpha(theme.palette.primary.main, 0.1)
                        : 'inherit',
                      '&:hover': {
                        backgroundColor: hasUnread
                          ? alpha(theme.palette.primary.main, 0.2)
                          : alpha(theme.palette.action.hover, 0.1)
                      },
                      position: 'relative'
                    }}
                  >
                    <ListItemAvatar>
                      <Badge 
                        badgeContent={conversation.unreadCount} 
                        color="error"
                        overlap="circular"
                      >
                        <Avatar
                          src={conversation.participantAvatar}
                          sx={{
                            bgcolor: hasUnread
                              ? alpha(theme.palette.primary.main, 0.2)
                              : alpha(theme.palette.action.active, 0.1)
                          }}
                        >
                          {conversation.participantName.charAt(0).toUpperCase()}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          color={hasUnread ? 'primary' : 'text.primary'}
                          sx={{
                            fontWeight: hasUnread ? 600 : 'normal',
                            pr: 4
                          }}
                        >
                          {conversation.participantName}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              pr: 4
                            }}
                          >
                            {lastMessage?.content || 'No messages yet'}
                          </Typography>
                          {lastMessage && (
                            <Typography variant="caption" color="text.secondary">
                              {formatDistanceToNow(new Date(lastMessage.timestamp), {
                                addSuffix: true
                              })}
                            </Typography>
                          )}
                        </Box>
                      }
                    />

                    <IconButton
                      size="small"
                      onClick={(e) => handleDeleteConversation(e, conversation.id)}
                      aria-label={`Delete conversation with ${conversation.participantName}`}
                      sx={{
                        position: 'absolute',
                        right: theme.spacing(1),
                        opacity: 0,
                        transition: theme.transitions.create(['opacity']),
                        '.MuiMenuItem-root:hover &': {
                          opacity: 1
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </MenuItem>
                );
              })}
            </Box>

            {/* Fixed "View All Messages" button at bottom */}
            <Divider />
            <Box sx={{
              p: 1,
              display: 'flex',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Button
                size="small"
                onClick={handleViewAll}
                fullWidth
              >
                View All Messages
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default MessagesMenu;
